import {useCallback, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {toast} from 'react-toastify'
import {URL} from '../../../gql/client'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {useRealtimeStore} from '../../../store/realtimeStore'
import {useGetUser} from '../../../store/userStore'

export type AiAssistantDomainConfig = {
  enabled: boolean
  label: string
}

export type AiAssistantConfig = {
  enabled: boolean
  domains: Record<string, AiAssistantDomainConfig>
}

export type AiAssistantThread = {
  id: string
  title: string
  last_activity: string
  is_active: boolean
}

export type AiAssistantMessage = {
  id: string
  direction: 'inbound' | 'outbound'
  content: string
  created: string
}

const EMPTY_CONFIG: AiAssistantConfig = {
  enabled: false,
  domains: {},
}

const sortThreads = (threads: AiAssistantThread[]) =>
  [...threads].sort(
    (left, right) =>
      new Date(right.last_activity).getTime() - new Date(left.last_activity).getTime()
  )

const upsertThread = (threads: AiAssistantThread[], thread: AiAssistantThread) => {
  const remaining = threads.filter((candidate) => candidate.id !== thread.id)
  return sortThreads([thread, ...remaining])
}

const upsertMessage = (messages: AiAssistantMessage[], message: AiAssistantMessage) => {
  const remaining = messages.filter((candidate) => candidate.id !== message.id)
  return [...remaining, message].sort(
    (left, right) => new Date(left.created).getTime() - new Date(right.created).getTime()
  )
}

const getReadableError = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data?.error
    if (typeof apiError === 'string' && apiError.trim()) {
      return apiError
    }
  }
  return fallback
}

export const useAiAssistant = () => {
  const user = useGetUser((state) => state.user)
  const token = useMemo(
    () => user?.token || window.localStorage.getItem(STORAGE_NAMES.TOKEN) || '',
    [user?.token]
  )
  const lastEventType = useRealtimeStore((state) => state.lastEventType)
  const lastEventPayload = useRealtimeStore((state) => state.lastEventPayload)
  const lastEventTimestamp = useRealtimeStore((state) => state.lastEventTimestamp)

  const [config, setConfig] = useState<AiAssistantConfig>(EMPTY_CONFIG)
  const [threads, setThreads] = useState<AiAssistantThread[]>([])
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [messages, setMessages] = useState<AiAssistantMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  )

  const activeThread = useMemo(
    () => threads.find((thread) => thread.id === activeThreadId) || null,
    [activeThreadId, threads]
  )

  const loadConfig = useCallback(async () => {
    if (!token) return
    try {
      const response = await axios.get<AiAssistantConfig>(`${URL}/api/ai-assistant/config`, {
        headers: authHeaders,
      })
      setConfig(response.data || EMPTY_CONFIG)
    } catch (error) {
      // Silently keep disabled defaults; the floating button simply won't render.
      console.error('Unable to load AI assistant config', error)
    }
  }, [authHeaders, token])

  const loadThreads = useCallback(
    async (limit?: number, offset?: number) => {
      if (!token) return
      setLoading(true)
      try {
        const response = await axios.get<{items: AiAssistantThread[]}>(
          `${URL}/api/ai-assistant/threads`,
          {
            headers: authHeaders,
            params: {limit, offset},
          }
        )
        setThreads(sortThreads(response.data.items || []))
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to load AI assistant conversations'))
      } finally {
        setLoading(false)
      }
    },
    [authHeaders, token]
  )

  const loadMessages = useCallback(
    async (threadId: string, limit?: number, offset?: number) => {
      if (!token) return
      setLoading(true)
      try {
        const response = await axios.get<{items: AiAssistantMessage[]}>(
          `${URL}/api/ai-assistant/threads/${threadId}/messages`,
          {
            headers: authHeaders,
            params: {limit, offset},
          }
        )
        setMessages(response.data.items || [])
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to load conversation messages'))
      } finally {
        setLoading(false)
      }
    },
    [authHeaders, token]
  )

  const createThread = useCallback(async () => {
    if (!token) return null
    try {
      const response = await axios.post<AiAssistantThread>(
        `${URL}/api/ai-assistant/threads`,
        {},
        {headers: authHeaders}
      )
      const nextThread = response.data
      setThreads((current) => upsertThread(current, nextThread))
      setActiveThreadId(nextThread.id)
      setMessages([])
      return nextThread
    } catch (error) {
      toast.error(getReadableError(error, 'Unable to start a new conversation'))
      return null
    }
  }, [authHeaders, token])

  const deleteThread = useCallback(
    async (threadId: string) => {
      if (!token) return false
      try {
        await axios.delete(`${URL}/api/ai-assistant/threads/${threadId}`, {
          headers: authHeaders,
        })
        setThreads((current) => current.filter((thread) => thread.id !== threadId))
        setActiveThreadId((current) => (current === threadId ? null : current))
        return true
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to delete the conversation'))
        return false
      }
    },
    [authHeaders, token]
  )

  const selectThread = useCallback((thread: AiAssistantThread | null) => {
    setActiveThreadId(thread ? thread.id : null)
    setMessages([])
  }, [])

  const sendMessage = useCallback(
    async (threadId: string, message: string) => {
      if (!token || !message.trim()) return false
      setSending(true)
      try {
        await axios.post<{message_id: string; status: string}>(
          `${URL}/api/ai-assistant/threads/${threadId}/messages`,
          {message},
          {headers: authHeaders}
        )
        // The POST only confirms enqueueing — the real answer arrives via websocket.
        return true
      } catch (error) {
        toast.error(getReadableError(error, 'Unable to send the message'))
        setSending(false)
        return false
      }
    },
    [authHeaders, token]
  )

  useEffect(() => {
    void loadConfig()
  }, [loadConfig])

  useEffect(() => {
    if (!activeThreadId) {
      setMessages([])
      return
    }
    void loadMessages(activeThreadId)
  }, [activeThreadId, loadMessages])

  useEffect(() => {
    if (!lastEventTimestamp || !lastEventType) return

    if (lastEventType === 'AI_ASSISTANT_MESSAGE_NEW' && lastEventPayload?.message) {
      const incomingMessage = lastEventPayload.message as AiAssistantMessage
      const incomingThreadId = String(lastEventPayload.thread_id || '')

      if (activeThreadId === incomingThreadId) {
        setMessages((current) => upsertMessage(current, incomingMessage))
        setSending(false)
      }

      setThreads((current) =>
        current.map((thread) =>
          thread.id === incomingThreadId
            ? {...thread, last_activity: incomingMessage.created}
            : thread
        )
      )
    }
  }, [activeThreadId, lastEventPayload, lastEventTimestamp, lastEventType])

  return {
    config,
    threads,
    activeThread,
    messages,
    loading,
    sending,
    loadThreads,
    createThread,
    deleteThread,
    selectThread,
    loadMessages,
    sendMessage,
    token,
  }
}
